import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axiosUserInstance from '../axiosUser';
import { createAxiosEntrepriseInstance } from '../axiosEntreprise';
import logo_seizo_s from '../images/svg/logo_seizo_s.svg';
import logo_seizo_nom from '../images/svg/logo_seizo_nom.svg';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let c of ca) {
        c = c.trim();
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
};

const getEntrepriseCode = () => {
    try {
        const raw = getCookie('currentEntrepriseInfos') || getCookie('_currentEntrepriseInfos');
        if (!raw) return null;
        // Lire le cookie : JSON brut (standard) ou encodé (ancienne session, compatibilité)
        let data;
        try { data = JSON.parse(raw); } catch (_) { data = JSON.parse(decodeURIComponent(raw)); }
        return data?.code ?? null;
    } catch {
        return null;
    }
};

const ROLE_LABELS = { 1: 'Super Admin', 2: 'Administrateur', 3: 'Manager', 4: 'Utilisateur' };

// Retourne le role_name depuis le cookie entreprise (issue #274)
const getRoleName = () => {
    try {
        const raw = getCookie('currentEntrepriseInfos') || getCookie('_currentEntrepriseInfos');
        if (!raw) return null;
        let data;
        try { data = JSON.parse(raw); } catch (_) { data = JSON.parse(decodeURIComponent(raw)); }
        return data?.role_name ?? data?.role?.name ?? null;
    } catch {
        return null;
    }
};

// ─── Composants UI réutilisables ──────────────────────────────────────────────

const Input = ({ label, name, value, onChange, type = 'text', disabled = false, placeholder = '' }) => (
    <div style={{ marginBottom: '1.2rem' }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--SEIZO-dark)', fontSize: '0.875rem' }}>
            {label}
        </label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            style={{
                width: '100%',
                padding: '0.6rem 0.9rem',
                borderRadius: '8px',
                border: '1.5px solid #dde3e9',
                fontSize: '0.95rem',
                background: disabled ? '#f5f7fa' : '#fff',
                color: disabled ? '#888' : 'var(--SEIZO-dark)',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
            }}
            onFocus={e => { if (!disabled) e.target.style.borderColor = 'var(--SEIZO-bleu)'; }}
            onBlur={e => { e.target.style.borderColor = '#dde3e9'; }}
        />
    </div>
);

const BtnPrimary = ({ children, onClick, loading = false, type = 'button', style = {} }) => (
    <button
        type={type}
        onClick={onClick}
        disabled={loading}
        style={{
            background: 'var(--SEIZO-bleu)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '0.6rem 1.4rem',
            fontWeight: 700,
            fontSize: '0.9rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'opacity 0.2s',
            ...style,
        }}
    >
        {loading ? 'Chargement...' : children}
    </button>
);

const BtnDanger = ({ children, onClick, style = {} }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            background: 'var(--SEIZO-rouge)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '0.4rem 1rem',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: 'pointer',
            ...style,
        }}
    >
        {children}
    </button>
);

const Alert = ({ type, message }) => {
    if (!message) return null;
    const colors = {
        success: { bg: '#e6f9ed', border: '#66BE63', text: '#1a6b2f' },
        error:   { bg: '#fdecea', border: '#F82121', text: '#8b1a1a' },
    };
    const c = colors[type] || colors.error;
    return (
        <div style={{
            background: c.bg, border: `1px solid ${c.border}`, color: c.text,
            borderRadius: '8px', padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.9rem',
        }}>
            {message}
        </div>
    );
};

const Card = ({ children, style = {} }) => (
    <div style={{
        background: '#fff', borderRadius: '12px', padding: '1.8rem',
        boxShadow: '0 2px 12px rgba(0,35,53,0.07)', marginBottom: '1.5rem', ...style,
    }}>
        {children}
    </div>
);

// ─── Onglet 1 — Mon Profil ────────────────────────────────────────────────────

const OngletProfil = ({ entrepriseCode }) => {
    const [profil, setProfil] = useState({ nom: '', prenom: '', email: '', fonction: '' });
    const [mdp, setMdp] = useState({ mot_de_passe_actuel: '', nouveau_mot_de_passe: '', nouveau_mot_de_passe_confirmation: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingMdp, setSavingMdp] = useState(false);
    const [alert, setAlert] = useState(null);
    const [alertMdp, setAlertMdp] = useState(null);

    const headers = entrepriseCode ? { 'X-Entreprise-Code': entrepriseCode } : {};

    useEffect(() => {
        setLoading(true);
        axiosUserInstance.get('/api/mon-compte/profil', { headers })
            .then(r => { setProfil(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [entrepriseCode]); // #498 — recharger quand l'espace actif change

    const handleSaveProfil = async () => {
        setSaving(true); setAlert(null);
        try {
            await axiosUserInstance.put('/api/mon-compte/profil', { nom: profil.nom, prenom: profil.prenom, fonction: profil.fonction }, { headers });
            setAlert({ type: 'success', message: 'Profil mis à jour avec succès.' });
        } catch {
            setAlert({ type: 'error', message: 'Erreur lors de la mise à jour.' });
        } finally { setSaving(false); }
    };

    const handleSaveMdp = async () => {
        setSavingMdp(true); setAlertMdp(null);
        if (mdp.nouveau_mot_de_passe !== mdp.nouveau_mot_de_passe_confirmation) {
            setAlertMdp({ type: 'error', message: 'Les mots de passe ne correspondent pas.' });
            setSavingMdp(false); return;
        }
        try {
            await axiosUserInstance.put('/api/mon-compte/mot-de-passe', mdp, { headers });
            setAlertMdp({ type: 'success', message: 'Mot de passe mis à jour.' });
            setMdp({ mot_de_passe_actuel: '', nouveau_mot_de_passe: '', nouveau_mot_de_passe_confirmation: '' });
        } catch (e) {
            setAlertMdp({ type: 'error', message: e.response?.data?.message || 'Erreur lors du changement.' });
        } finally { setSavingMdp(false); }
    };

    if (loading) return <p style={{ color: '#888' }}>Chargement...</p>;

    return (
        <div>
            <Card>
                <h3 style={{ marginTop: 0, marginBottom: '1.4rem', color: 'var(--SEIZO-dark)', fontSize: '1.1rem' }}>Informations personnelles</h3>
                <Alert type={alert?.type} message={alert?.message} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>
                    <Input label="Nom" name="nom" value={profil.nom || ''} onChange={e => setProfil({ ...profil, nom: e.target.value })} />
                    <Input label="Prénom" name="prenom" value={profil.prenom || ''} onChange={e => setProfil({ ...profil, prenom: e.target.value })} />
                </div>
                <Input label="Email" name="email" value={profil.email || ''} disabled />
                <Input label="Fonction" name="fonction" value={profil.fonction || ''} onChange={e => setProfil({ ...profil, fonction: e.target.value })} placeholder="Ex: Responsable production" />
                <BtnPrimary onClick={handleSaveProfil} loading={saving}>Enregistrer</BtnPrimary>
            </Card>

        </div>
    );
};

// ─── Onglet 2 — Mon Espace ────────────────────────────────────────────────

const OngletEntreprise = ({ entrepriseCode }) => {
    const history = useHistory();
    const [data, setData] = useState(null);
    const [libelle, setLibelle] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState(null);

    // Stripe
    const [abonnement, setAbonnement] = useState(null);
    const [loadingAbo, setLoadingAbo] = useState(false);
    const [nbEcransSup, setNbEcransSup] = useState(0);
    const [savingEcrans, setSavingEcrans] = useState(false);
    const [loadingPortail, setLoadingPortail] = useState(false);
    const [loadingResilier, setLoadingResilier] = useState(false);
    const [loadingReactiver, setLoadingReactiver] = useState(false);
    const [showConfirmEcrans, setShowConfirmEcrans] = useState(false); // #489 modale confirmation facturation
    const [showChangerPlan, setShowChangerPlan] = useState(false);    // #259 modale changer de plan
    const [changerPlanKey, setChangerPlanKey] = useState('');          // #259 plan cible
    const [loadingChangerPlan, setLoadingChangerPlan] = useState(false); // #259

    const headers = entrepriseCode ? { 'X-Entreprise-Code': entrepriseCode } : {};
    const canEdit = data?.role?.id <= 2;
    const isAdmin = data?.role?.id <= 2;

    useEffect(() => {
        setLoading(true);
        axiosUserInstance.get('/api/mon-compte/espace', { headers })
            .then(r => { setData(r.data); setLibelle(r.data.libelle); setLoading(false); })
            .catch(() => setLoading(false));
    }, [entrepriseCode]); // #498 — recharger quand l'espace actif change

    useEffect(() => {
        if (!loading && data) {
            setLoadingAbo(true);
            axiosUserInstance.get('/api/stripe/abonnement', { headers })
                .then(r => {
                    const abo = r.data.abonnement;
                    setAbonnement(abo);
                    setNbEcransSup(abo?.nb_ecrans_supp || 0);

                    // #260 — Synchroniser le cookie currentEntrepriseInfos avec le quota réel
                    if (abo && (abo.nb_max_totem !== undefined || abo.isFreemium !== undefined)) {
                        try {
                            const raw = getCookie('currentEntrepriseInfos') || getCookie('_currentEntrepriseInfos');
                            if (raw) {
                                // Lire le cookie : JSON brut (standard) ou encodé (ancienne version)
                                let infos;
                                try { infos = JSON.parse(raw); } catch (_) { infos = JSON.parse(decodeURIComponent(raw)); }
                                if (abo.nb_max_totem !== undefined) infos.nb_max_totem = abo.nb_max_totem;
                                if (abo.isFreemium !== undefined)   infos.isFreemium   = abo.isFreemium;
                                // Toujours écrire en JSON brut — cohérent avec LoginStore, Totem et Dashboard
                                document.cookie = `currentEntrepriseInfos=${JSON.stringify(infos)};path=/;SameSite=None;Secure;max-age=86400`;
                            }
                        } catch (_) { /* ignore */ }
                    }
                })
                .catch(() => {})
                .finally(() => setLoadingAbo(false));
        }
    }, [loading, data]);

    const handleSave = async () => {
        setSaving(true); setAlert(null);
        try {
            await axiosUserInstance.put('/api/mon-compte/espace', { libelle }, { headers });
            setData({ ...data, libelle });
            setAlert({ type: 'success', message: "Nom de l'espace mis à jour." });
        } catch {
            setAlert({ type: 'error', message: 'Erreur lors de la mise à jour.' });
        } finally { setSaving(false); }
    };

    // #489 — Ouvrir la modale de confirmation avant facturation
    const handleUpdateEcrans = () => {
        if (nbEcransSup === 0) {
            setAlert({ type: 'error', message: 'Veuillez sélectionner au moins 1 écran supplémentaire.' });
            return;
        }
        setShowConfirmEcrans(true);
    };

    // #489 — Confirmer et exécuter la mise à jour des écrans après validation
    const handleConfirmUpdateEcrans = async () => {
        setShowConfirmEcrans(false);
        setSavingEcrans(true); setAlert(null);
        try {
            const res = await axiosUserInstance.post('/api/stripe/abonnement/ecrans', { nb_ecrans_supp: nbEcransSup }, { headers });
            setAlert({ type: 'success', message: `Écrans mis à jour : ${res.data.nb_ecrans_total} écran(s) actif(s).` });
            const r = await axiosUserInstance.get('/api/stripe/abonnement', { headers });
            setAbonnement(r.data.abonnement);
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de la mise à jour des écrans.' });
        } finally { setSavingEcrans(false); }
    };

    const handlePortail = async () => {
        setLoadingPortail(true);
        try {
            const res = await axiosUserInstance.post('/api/stripe/abonnement/portail', {}, { headers });
            window.open(res.data.url, '_blank');
        } catch {
            setAlert({ type: 'error', message: "Impossible d'ouvrir le portail de facturation." });
        } finally { setLoadingPortail(false); }
    };

    const handleResilier = async () => {
        if (!window.confirm("Confirmer la résiliation ? Votre abonnement restera actif jusqu'à la fin de la période en cours.")) return;
        setLoadingResilier(true);
        try {
            await axiosUserInstance.post('/api/stripe/abonnement/resilier', {}, { headers });
            setAlert({ type: 'success', message: "Résiliation programmée. Votre accès reste actif jusqu'à la fin de la période." });
            setAbonnement({ ...abonnement, cancel_at_period_end: true });
        } catch {
            setAlert({ type: 'error', message: 'Erreur lors de la résiliation.' });
        } finally { setLoadingResilier(false); }
    };

    const handleReactiver = async () => {
        setLoadingReactiver(true);
        try {
            await axiosUserInstance.post('/api/stripe/abonnement/reactiver', {}, { headers });
            setAlert({ type: 'success', message: 'Abonnement réactivé avec succès.' });
            setAbonnement({ ...abonnement, cancel_at_period_end: false });
        } catch {
            setAlert({ type: 'error', message: 'Erreur lors de la réactivation.' });
        } finally { setLoadingReactiver(false); }
    };

    // #259 — Changer de plan (upgrade/downgrade) sans nouveau checkout
    const handleConfirmChangerPlan = async () => {
        if (!changerPlanKey) return;
        setLoadingChangerPlan(true);
        try {
            const res = await axiosUserInstance.post('/api/stripe/abonnement/changer-plan', { plan_key: changerPlanKey }, { headers });
            // Recharger l’état réellement confirmé par le backend/Stripe plutôt que supposer un succès local.
            const refreshed = await axiosUserInstance.get('/api/stripe/abonnement', { headers });
            const abo = refreshed.data.abonnement;
            setAbonnement(abo);
            setNbEcransSup(abo?.nb_ecrans_supp || 0);
            setData(current => ({
                ...current,
                isFreemium: abo?.isFreemium ?? res.data.isFreemium,
                nb_max_totem: abo?.nb_max_totem ?? res.data.nb_max_totem,
            }));
            setAlert({ type: 'success', message: `Plan mis à jour vers ${planLabels[res.data.plan_key] || res.data.plan_key}.` });
            setShowChangerPlan(false);
            setChangerPlanKey('');
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.message || 'Erreur lors du changement de plan.' });
        } finally { setLoadingChangerPlan(false); }
    };

    if (loading) return <p style={{ color: '#888' }}>Chargement...</p>;
    if (!data) return <p style={{ color: '#888' }}>Impossible de charger les informations.</p>;

    const planLabels = { basique: 'Offre basique', premium: 'Premium', industriel: 'Site industriel' };
    const planKey = abonnement?.plan_key || data.licence?.offre || null;
    const badgeColor = data.isFreemium ? '#FF9436' : '#66BE63';
    const badgeLabel = data.isFreemium
        ? 'Freemium'
        : abonnement?.statut === 'trialing'
            ? 'Essai gratuit'
            : (planLabels[planKey] || 'Premium');

    return (
        <div>
            {/* #489 — Modale confirmation facturation écrans */}
            {showConfirmEcrans && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <h3 style={{ marginTop: 0, color: 'var(--SEIZO-dark)' }}>Confirmer la modification</h3>
                        <p style={{ color: '#555', fontSize: '0.92rem', lineHeight: 1.6 }}>
                            Vous êtes sur le point de modifier votre abonnement à <strong>{nbEcransSup} écran(s) supplémentaire(s)</strong>.
                            <br /><br />
                            Montant additionnel : <strong style={{ color: 'var(--SEIZO-bleu)' }}>{nbEcransSup * 39}€/mois</strong> (facturation au prorata immédiate).
                        </p>
                        <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                            <button onClick={() => setShowConfirmEcrans(false)} style={{ background: 'none', border: '1.5px solid #dde3e9', borderRadius: '8px', padding: '0.5rem 1.2rem', cursor: 'pointer', fontWeight: 600, color: '#555' }}>Annuler</button>
                            <BtnPrimary onClick={handleConfirmUpdateEcrans} loading={savingEcrans}>Confirmer</BtnPrimary>
                        </div>
                    </div>
                </div>
            )}

            {/* #259 — Modale changer de plan */}
            {showChangerPlan && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', maxWidth: '460px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <h3 style={{ marginTop: 0, color: 'var(--SEIZO-dark)' }}>Changer de plan</h3>
                        <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '0.6rem' }}>Plan actuel : <strong>{planLabels[abonnement?.plan_key] || '—'}</strong></p>
                        <p style={{ color: '#6B7A87', fontSize: '0.82rem', marginTop: 0, marginBottom: '1.2rem', lineHeight: 1.45 }}>Les changements entre offres payantes sont appliqués immédiatement. Pour revenir à l’offre basique, résiliez votre abonnement : vos droits payants restent actifs jusqu’à la fin de la période en cours.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.5rem' }}>
                            {Object.entries(planLabels).filter(([key]) => key !== abonnement?.plan_key && key !== 'basique').map(([key, label]) => (
                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.8rem 1rem', borderRadius: '10px', border: changerPlanKey === key ? '2px solid var(--SEIZO-bleu)' : '1.5px solid #dde3e9', cursor: 'pointer', background: changerPlanKey === key ? '#f0f6ff' : '#fff' }}>
                                    <input type="radio" name="plan" value={key} checked={changerPlanKey === key} onChange={() => setChangerPlanKey(key)} style={{ accentColor: 'var(--SEIZO-bleu)' }} />
                                    <span style={{ fontWeight: 600, color: 'var(--SEIZO-dark)' }}>{label}</span>
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowChangerPlan(false)} style={{ background: 'none', border: '1.5px solid #dde3e9', borderRadius: '8px', padding: '0.5rem 1.2rem', cursor: 'pointer', fontWeight: 600, color: '#555' }}>Annuler</button>
                            <BtnPrimary onClick={handleConfirmChangerPlan} loading={loadingChangerPlan} disabled={!changerPlanKey}>Confirmer le changement</BtnPrimary>
                        </div>
                    </div>
                </div>
            )}

            {/* Informations entreprise */}
            <Card>
                <h3 style={{ marginTop: 0, marginBottom: '1.4rem', color: 'var(--SEIZO-dark)', fontSize: '1.1rem' }}>Informations de l'espace</h3>
                <Alert type={alert?.type} message={alert?.message} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    <span style={{ background: badgeColor, color: '#fff', borderRadius: '20px', padding: '0.3rem 1rem', fontWeight: 700, fontSize: '0.85rem' }}>
                        {badgeLabel}
                    </span>
                    {data.role && (
                        <span style={{ background: '#e8f4fd', color: 'var(--SEIZO-bleu)', borderRadius: '20px', padding: '0.3rem 1rem', fontWeight: 600, fontSize: '0.85rem' }}>
                            {ROLE_LABELS[data.role.id] || data.role.name}
                        </span>
                    )}
                    {abonnement?.cancel_at_period_end && (
                        <span style={{ background: '#fdecea', color: '#F82121', borderRadius: '20px', padding: '0.3rem 1rem', fontWeight: 600, fontSize: '0.82rem' }}>
                            Résiliation prévue le {abonnement.current_period_end}
                        </span>
                    )}
                </div>

                <Input
                    label="Nom de l'espace"
                    name="libelle"
                    value={libelle}
                    onChange={e => setLibelle(e.target.value)}
                    disabled={!canEdit}
                />
                {canEdit && <BtnPrimary onClick={handleSave} loading={saving}>Enregistrer</BtnPrimary>}
            </Card>

            {/* Offre & Écrans */}
            <Card>
                <h3 style={{ marginTop: 0, marginBottom: '1.2rem', color: 'var(--SEIZO-dark)', fontSize: '1.1rem' }}>Offre & Écrans</h3>

                {loadingAbo ? (
                    <p style={{ color: '#888', fontSize: '0.9rem' }}>Chargement de l'abonnement...</p>
                ) : (
                    <>
                        {/* Métriques */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                            {[
                                { label: 'Offre souscrite', value: planLabels[abonnement?.plan_key] || data.licence?.offre || '—' },
                                { label: 'Écrans actifs', value: `${data.nb_ecrans} / ${data.nb_max_totem}` },
                                { label: 'Prochain renouvellement', value: abonnement?.current_period_end || data.licence?.date_fin || 'illimitée' },
                            ].map(item => (
                                <div key={item.label} style={{ background: '#f5f8fc', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.4rem' }}>{item.label}</div>
                                    <div style={{ fontWeight: 700, color: 'var(--SEIZO-dark)', fontSize: '1rem' }}>{item.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Essai gratuit */}
                        {abonnement?.trial_end && (
                            <div style={{ background: '#e6f9ed', borderRadius: '10px', padding: '0.8rem 1rem', marginBottom: '1.2rem', fontSize: '0.88rem', color: '#1a6b2f' }}>
                                ✓ Essai gratuit actif jusqu'au <strong>{abonnement.trial_end}</strong> — aucun débit avant cette date.
                            </div>
                        )}

                        {/* Montant mensuel */}
                        {abonnement && (
                            <div style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#555' }}>
                                Montant mensuel : <strong style={{ color: 'var(--SEIZO-dark)', fontSize: '1.1rem' }}>{abonnement.montant_mensuel}€</strong>
                                {abonnement.nb_ecrans_supp > 0 && (
                                    <span style={{ color: '#888', fontSize: '0.82rem' }}> (dont {abonnement.nb_ecrans_supp} écran(s) supplémentaire(s) × 39€)</span>
                                )}
                            </div>
                        )}

                        {/* Gestion écrans supplémentaires */}
                        {isAdmin && abonnement && (
                            <div style={{ background: '#f5f8fc', borderRadius: '12px', padding: '1.2rem', marginBottom: '1.5rem' }}>
                                <div style={{ fontWeight: 700, color: 'var(--SEIZO-dark)', marginBottom: '0.8rem', fontSize: '0.9rem' }}>Ajouter des écrans supplémentaires</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                        <button onClick={() => setNbEcransSup(Math.max(0, nbEcransSup - 1))} style={{
                                            width: '30px', height: '30px', borderRadius: '50%', border: '2px solid #dde3e9',
                                            background: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '1rem',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>−</button>
                                        <span style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--SEIZO-bleu)', minWidth: '24px', textAlign: 'center' }}>{nbEcransSup}</span>
                                        <button onClick={() => setNbEcransSup(nbEcransSup + 1)} style={{
                                            width: '30px', height: '30px', borderRadius: '50%', border: '2px solid var(--SEIZO-bleu)',
                                            background: 'var(--SEIZO-bleu)', fontWeight: 700, cursor: 'pointer', fontSize: '1rem',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                                        }}>+</button>
                                    </div>
                                    <span style={{ fontSize: '0.85rem', color: '#888' }}>écran(s) sup. × 39€/mois</span>
                                    <BtnPrimary onClick={handleUpdateEcrans} loading={savingEcrans} style={{ marginLeft: 'auto' }}>
                                        Appliquer
                                    </BtnPrimary>
                                </div>
                            </div>
                        )}

                        {/* Actions abonnement */}
                        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {!abonnement && (
                                <BtnPrimary onClick={() => history.push('/pricing')}>
                                    Voir les offres →
                                </BtnPrimary>
                            )}
                            {abonnement && isAdmin && (
                                <BtnPrimary onClick={handlePortail} loading={loadingPortail}>
                                    Gérer la facturation
                                </BtnPrimary>
                            )}
                            {/* #259 — Bouton changer de plan */}
                            {abonnement && isAdmin && (
                                <button onClick={() => { setChangerPlanKey(''); setShowChangerPlan(true); }} style={{
                                    background: 'none', border: '1.5px solid var(--SEIZO-bleu)', color: 'var(--SEIZO-bleu)',
                                    borderRadius: '8px', padding: '0.55rem 1.2rem', fontWeight: 600,
                                    fontSize: '0.88rem', cursor: 'pointer',
                                }}>
                                    Changer de plan
                                </button>
                            )}
                            {abonnement && isAdmin && !abonnement.cancel_at_period_end && (
                                <button onClick={handleResilier} disabled={loadingResilier} style={{
                                    background: 'none', border: '1.5px solid #F82121', color: '#F82121',
                                    borderRadius: '8px', padding: '0.55rem 1.2rem', fontWeight: 600,
                                    fontSize: '0.88rem', cursor: 'pointer', opacity: loadingResilier ? 0.6 : 1,
                                }}>
                                    {loadingResilier ? 'Traitement...' : "Résilier l'abonnement"}
                                </button>
                            )}
                            {abonnement && isAdmin && abonnement.cancel_at_period_end && (
                                <BtnPrimary onClick={handleReactiver} loading={loadingReactiver}>
                                    Annuler la résiliation
                                </BtnPrimary>
                            )}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

// ─── Onglet 3 — Mon Équipe ────────────────────────────────────────────────────

const OngletEquipe = ({ entrepriseCode }) => {
    const [membres, setMembres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [showInvite, setShowInvite] = useState(false);
    const [invite, setInvite] = useState({ email: '', nom: '', prenom: '', role_id: '3', workspaces_ids: [] });
    const [workspacesDispos, setWorkspacesDispos] = useState([]);
    // Charger les workspaces pour la sélection (utilisateur uniquement)
    React.useEffect(() => {
        if (invite.role_id === '4') {
            axiosUserInstance.get('/api/workspaces', { headers })
                .then(r => setWorkspacesDispos(r.data.data || []))
                .catch(() => {});
        }
    }, [invite.role_id]);
    const [inviting, setInviting] = useState(false);


    const headers = entrepriseCode ? { 'X-Entreprise-Code': entrepriseCode } : {};
    const axiosEnt = createAxiosEntrepriseInstance(entrepriseCode);

    const fetchEquipe = useCallback(() => {
        setLoading(true);
        axiosUserInstance.get('/api/mon-compte/equipe', { headers })
            .then(r => { setMembres(r.data.membres); setLoading(false); })
            .catch(() => setLoading(false));
    }, [entrepriseCode]);

    useEffect(() => {
        fetchEquipe();

    }, [fetchEquipe]);

    const handleInviter = async () => {
        setInviting(true); setAlert(null);
        try {
            const inviteData = {
                email:   invite.email,
                nom:     invite.nom,
                prenom:  invite.prenom,
                role_id: parseInt(invite.role_id),
            };
            // Pour les utilisateurs (role_id=4), envoyer la liste des workspaces sélectionnés
            if (invite.role_id === '4' && invite.workspaces_ids.length > 0) {
                inviteData.workspaces = invite.workspaces_ids.map(id => ({ id, role: 'viewer' }));
            }
            await axiosUserInstance.post('/api/mon-compte/equipe/inviter', inviteData, { headers });
            setAlert({ type: 'success', message: `Invitation envoyée à ${invite.email}.` });
            setShowInvite(false);
            setInvite({ email: '', nom: '', prenom: '', role_id: '3', workspaces_ids: [] });
            fetchEquipe();
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de l\'invitation.' });
        } finally { setInviting(false); }
    };

    const handleRetirer = async (userId, email) => {
        if (!window.confirm(`Retirer ${email} de l'entreprise ?`)) return;
        try {
            await axiosUserInstance.delete(`/api/mon-compte/equipe/${userId}`, { headers });
            setAlert({ type: 'success', message: `${email} a été retiré.` });
            fetchEquipe();
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur.' });
        }
    };

    const handleChangeRole = async (userId, roleId) => {
        try {
            await axiosUserInstance.put(`/api/mon-compte/equipe/${userId}/role`, { role_id: parseInt(roleId) }, { headers });
            setAlert({ type: 'success', message: 'Rôle mis à jour.' });
            fetchEquipe();
        } catch (e) {
            const msg = e.response?.data?.message || 'Erreur lors du changement de rôle.';
            setAlert({ type: 'error', message: msg });
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: 'var(--SEIZO-dark)', fontSize: '1.1rem' }}>
                    Membres ({membres.length})
                </h3>
                <BtnPrimary onClick={() => setShowInvite(!showInvite)}>
                    {showInvite ? 'Annuler' : '+ Inviter un collaborateur'}
                </BtnPrimary>
            </div>

            <Alert type={alert?.type} message={alert?.message} />

            {showInvite && (
                <Card style={{ border: '2px solid var(--SEIZO-bleu)' }}>
                    <h4 style={{ marginTop: 0, color: 'var(--SEIZO-dark)' }}>Inviter un collaborateur</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>
                        <Input label="Prénom" name="prenom" value={invite.prenom} onChange={e => setInvite({ ...invite, prenom: e.target.value })} />
                        <Input label="Nom" name="nom" value={invite.nom} onChange={e => setInvite({ ...invite, nom: e.target.value })} />
                    </div>
                    <Input label="Email *" name="email" type="email" value={invite.email} onChange={e => setInvite({ ...invite, email: e.target.value })} />
                    <div style={{ marginBottom: '1.2rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--SEIZO-dark)', fontSize: '0.875rem' }}>Rôle *</label>
                        <select
                            value={invite.role_id}
                            onChange={e => setInvite({ ...invite, role_id: e.target.value })}
                            style={{ width: '100%', padding: '0.6rem 0.9rem', borderRadius: '8px', border: '1.5px solid #dde3e9', fontSize: '0.95rem', background: '#fff' }}
                        >
                            <option value="2">Administrateur</option>
                            <option value="3">Manager</option>
                            <option value="4">Utilisateur</option>
                        </select>
                    </div>
                    {invite.role_id === '4' ? (
                        <div style={{ marginBottom: '1.2rem' }}>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--SEIZO-dark)', fontSize: '0.875rem' }}>
                                Workspaces accessibles *
                            </label>
                            <p style={{ fontSize: '0.82rem', color: '#70787B', margin: '0 0 0.8rem', lineHeight: 1.5 }}>
                                Sélectionnez les workspaces auxquels cet utilisateur aura accès.
                            </p>
                            {workspacesDispos.length === 0 ? (
                                <p style={{ fontSize: '0.85rem', color: '#8A9BAB' }}>Chargement des workspaces...</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {workspacesDispos.map(ws => (
                                        <label key={ws.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontSize: '0.9rem', padding: '0.5rem 0.8rem', borderRadius: 6, border: '1.5px solid', borderColor: invite.workspaces_ids.includes(ws.id) ? 'var(--SEIZO-bleu)' : '#dde3e9', background: invite.workspaces_ids.includes(ws.id) ? 'rgba(0,176,255,0.06)' : '#fff' }}>
                                            <input
                                                type="checkbox"
                                                checked={invite.workspaces_ids.includes(ws.id)}
                                                onChange={e => {
                                                    const ids = e.target.checked
                                                        ? [...invite.workspaces_ids, ws.id]
                                                        : invite.workspaces_ids.filter(id => id !== ws.id);
                                                    setInvite({ ...invite, workspaces_ids: ids });
                                                }}
                                            />
                                            <span style={{ fontWeight: 600, color: 'var(--SEIZO-dark)' }}>{ws.name}</span>
                                            {ws.ecrans_count !== undefined && <span style={{ fontSize: '0.8rem', color: '#8A9BAB' }}>{ws.ecrans_count} écran(s)</span>}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <p style={{ fontSize: '0.82rem', color: '#70787B', margin: '0 0 1rem', padding: '8px 12px', background: 'rgba(0,176,255,0.06)', borderRadius: 6, borderLeft: '3px solid rgba(0,176,255,0.4)' }}>
                            Admin et Manager ont accès à tous les workspaces automatiquement.
                        </p>
                    )}
                    <BtnPrimary onClick={handleInviter} loading={inviting}>Envoyer l'invitation</BtnPrimary>
                </Card>
            )}

            {loading ? (
                <p style={{ color: '#888' }}>Chargement...</p>
            ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                            <tr style={{ background: 'var(--SEIZO-dark)', color: '#fff' }}>
                                {['Membre', 'Email', 'Fonction', 'Rôle', 'Dernière connexion', ''].map((h, idx) => (
                                    <th key={h} style={{ padding: '0.9rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: idx === 5 ? '90px' : undefined }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {membres.map((m, i) => (
                                <tr key={m.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fbfd', borderBottom: '1px solid #eef1f5' }}>
                                    <td style={{ padding: '0.8rem 1rem', fontWeight: 600, color: 'var(--SEIZO-dark)' }}>
                                        {m.prenom} {m.nom}
                                    </td>
                                    <td style={{ padding: '0.8rem 1rem', color: '#555' }}>{m.email}</td>
                                    <td style={{ padding: '0.8rem 1rem', color: '#888' }}>{m.fonction || '—'}</td>
                                    <td style={{ padding: '0.8rem 1rem' }}>
                                        <select
                                            value={m.role?.id || 4}
                                            onChange={e => handleChangeRole(m.id, e.target.value)}
                                            style={{ padding: '0.3rem 0.6rem', borderRadius: '6px', border: '1px solid #dde3e9', fontSize: '0.85rem', background: '#fff' }}
                                        >
                                            <option value="2">Administrateur</option>
                                            <option value="3">Manager</option>
                                            <option value="4">Utilisateur</option>
                                        </select>
                                    </td>
                                    <td style={{ padding: '0.8rem 1rem', color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                        {m.derniere_connexion ? new Date(m.derniere_connexion).toLocaleDateString('fr-FR') : '—'}
                                    </td>
                                    <td style={{ padding: '0.8rem 1rem', whiteSpace: 'nowrap' }}>
                                        <BtnDanger onClick={() => handleRetirer(m.id, m.email)}>Retirer</BtnDanger>
                                    </td>
                                </tr>
                            ))}
                            {membres.length === 0 && (
                                <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>Aucun membre trouvé.</td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                </Card>
            )}
        </div>
    );
};

// ─── Onglet 4 — Workspaces & Droits (#263) ──────────────────────────────────────

const ROLE_WS_LABELS = { admin: 'Administrateur', manager: 'Manager', viewer: 'Lecteur' };
const ROLE_WS_COLORS = { admin: '#00B0FF', manager: '#FF9800', viewer: '#8A9BAB' };

const OngletWorkspaces = ({ entrepriseCode }) => {
    const [workspaces, setWorkspaces]     = useState([]);
    const [wsSelectionne, setWsSelectionne] = useState(null);
    const [membres, setMembres]           = useState([]);
    const [membresOrg, setMembresOrg]     = useState([]);
    const [totemsWs, setTotemsWs]         = useState([]);
    const [totemsOrg, setTotemsOrg]       = useState([]);
    const [loading, setLoading]           = useState(true);
    const [loadingMembres, setLoadingMembres] = useState(false);
    const [loadingTotems, setLoadingTotems] = useState(false);
    const [alert, setAlert]               = useState(null);
    const [showAjouter, setShowAjouter]   = useState(false);
    const [ajout, setAjout]               = useState({ user_id: '', role: 'viewer', can_manage_totems: false, can_invite_members: false });
    const [ajoutEnCours, setAjoutEnCours] = useState(false);
    const [editEcrans, setEditEcrans]     = useState(false);
    const [selectedTotemIds, setSelectedTotemIds] = useState([]);
    const [savingEcrans, setSavingEcrans] = useState(false);

    const axios = createAxiosEntrepriseInstance(entrepriseCode);

    // Charger la liste des workspaces
    const fetchWorkspaces = useCallback(() => {
        setLoading(true);
        axios.get('/api/workspaces')
            .then(r => {
                setWorkspaces(r.data.data || []);
                setLoading(false);
                if (!wsSelectionne && r.data.data?.length > 0) {
                    setWsSelectionne(r.data.data[0]);
                }
            })
            .catch(() => setLoading(false));
    }, [entrepriseCode]);

    // Charger les membres de l'organisation (pour le formulaire d'ajout)
    const fetchMembresOrg = useCallback(() => {
        axiosUserInstance.get('/api/mon-compte/equipe', entrepriseCode ? { headers: { 'X-Entreprise-Code': entrepriseCode } } : {})
            .then(r => setMembresOrg(r.data.membres || []))
            .catch(() => {});
    }, [entrepriseCode]);

    // Charger les membres du workspace sélectionné
    const fetchMembresWs = useCallback((ws) => {
        if (!ws) return;
        setLoadingMembres(true);
        axios.get(`/api/workspaces/${ws.id}/members`)
            .then(r => { setMembres(r.data.data || []); setLoadingMembres(false); })
            .catch(() => setLoadingMembres(false));
    }, [entrepriseCode]);

    // Les deux appels existent déjà côté API : ils rendent visible la vraie
    // affectation des écrans du workspace, au lieu du seul compteur historique.
    const fetchTotemsWs = useCallback((ws) => {
        if (!ws) return;
        setLoadingTotems(true);
        axios.get(`/api/workspaces/${ws.id}/totems`)
            .then(r => {
                const list = r.data.data || [];
                setTotemsWs(list);
                setSelectedTotemIds(list.map(totem => String(totem.id_totem)));
                setLoadingTotems(false);
            })
            .catch(() => {
                setTotemsWs([]);
                setSelectedTotemIds([]);
                setLoadingTotems(false);
            });
    }, [entrepriseCode]);

    const fetchTotemsOrg = useCallback(() => {
        axios.get('/api/getAllTotem')
            .then(r => setTotemsOrg(Array.isArray(r.data) ? r.data : (r.data.data || [])))
            .catch(() => setTotemsOrg([]));
    }, [entrepriseCode]);

    useEffect(() => { fetchWorkspaces(); fetchMembresOrg(); fetchTotemsOrg(); }, [fetchWorkspaces, fetchMembresOrg, fetchTotemsOrg]);
    useEffect(() => { if (wsSelectionne) fetchMembresWs(wsSelectionne); }, [wsSelectionne, fetchMembresWs]);
    useEffect(() => { if (wsSelectionne) fetchTotemsWs(wsSelectionne); }, [wsSelectionne, fetchTotemsWs]);

    const handleSelectWs = (ws) => {
        setWsSelectionne(ws);
        setShowAjouter(false);
        setEditEcrans(false);
        setAlert(null);
    };

    const handleChangerRole = async (userId, role) => {
        setAlert(null);
        const permsDefaut = {
            admin:   { can_manage_totems: true,  can_invite_members: true  },
            manager: { can_manage_totems: true,  can_invite_members: true  },
            viewer:  { can_manage_totems: false, can_invite_members: false },
        };
        try {
            await axios.put(`/api/workspaces/${wsSelectionne.id}/members/${userId}`, {
                role,
                ...permsDefaut[role],
            });
            setAlert({ type: 'success', message: 'Droits mis à jour.' });
            fetchMembresWs(wsSelectionne);
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de la mise à jour.' });
        }
    };

    const handleTogglePerm = async (userId, perm, valeurActuelle) => {
        setAlert(null);
        try {
            await axios.put(`/api/workspaces/${wsSelectionne.id}/members/${userId}`, { [perm]: !valeurActuelle });
            setAlert({ type: 'success', message: 'Permission mise à jour.' });
            fetchMembresWs(wsSelectionne);
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur.' });
        }
    };

    const handleRetirerMembre = async (userId, email) => {
        if (!window.confirm(`Retirer ${email} du workspace "${wsSelectionne.name}" ?`)) return;
        try {
            await axios.delete(`/api/workspaces/${wsSelectionne.id}/members/${userId}`);
            setAlert({ type: 'success', message: `${email} a été retiré du workspace.` });
            fetchMembresWs(wsSelectionne);
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur.' });
        }
    };

    const handleAjouterMembre = async () => {
        if (!ajout.user_id) { setAlert({ type: 'error', message: 'Veuillez sélectionner un membre.' }); return; }
        setAjoutEnCours(true); setAlert(null);
        try {
            await axios.post(`/api/workspaces/${wsSelectionne.id}/members`, {
                user_id: parseInt(ajout.user_id),
                role: ajout.role,
                can_manage_totems: ajout.can_manage_totems,
                can_invite_members: ajout.can_invite_members,
            });
            setAlert({ type: 'success', message: 'Membre ajouté au workspace.' });
            setShowAjouter(false);
            setAjout({ user_id: '', role: 'viewer', can_manage_totems: false, can_invite_members: false });
            fetchMembresWs(wsSelectionne);
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de l\'ajout.' });
        } finally { setAjoutEnCours(false); }
    };

    const handleToggleTotem = (totemId) => {
        const id = String(totemId);
        setSelectedTotemIds(current => current.includes(id)
            ? current.filter(currentId => currentId !== id)
            : [...current, id]);
    };

    const handleSaveTotems = async () => {
        if (!wsSelectionne) return;
        setSavingEcrans(true);
        setAlert(null);
        try {
            const res = await axios.post(`/api/workspaces/${wsSelectionne.id}/totems/assign`, {
                totem_ids: selectedTotemIds,
            });
            setAlert({ type: 'success', message: res.data.message || 'Écrans du workspace mis à jour.' });
            setEditEcrans(false);
            setWsSelectionne(current => current ? { ...current, totems_count: selectedTotemIds.length } : current);
            fetchTotemsWs(wsSelectionne);
            fetchWorkspaces();
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de la mise à jour des écrans.' });
        } finally {
            setSavingEcrans(false);
        }
    };

    // Membres de l'org non encore dans le workspace
    const membresDisponibles = membresOrg.filter(m => !membres.find(mb => mb.id === m.id));
    const workspaceLabelForTotem = (totem) => {
        if (!totem.workspace_id || Number(totem.workspace_id) === Number(wsSelectionne?.id)) return null;
        return workspaces.find(ws => Number(ws.id) === Number(totem.workspace_id))?.name || 'un autre workspace';
    };

    if (loading) return <p style={{ color: '#888' }}>Chargement des workspaces...</p>;
    if (workspaces.length === 0) return <p style={{ color: '#888' }}>Aucun workspace trouvé.</p>;

    return (
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
            {/* Colonne gauche : liste des workspaces */}
            <div style={{ width: '220px', flexShrink: 0 }}>
                <h4 style={{ margin: '0 0 0.75rem', color: 'var(--SEIZO-dark)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Workspaces</h4>
                {workspaces.map(ws => (
                    <button
                        key={ws.id}
                        onClick={() => handleSelectWs(ws)}
                        style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '0.65rem 0.9rem', marginBottom: '0.4rem',
                            background: wsSelectionne?.id === ws.id ? '#EAF6FF' : '#fff',
                            border: wsSelectionne?.id === ws.id ? '2px solid var(--SEIZO-bleu)' : '1.5px solid #dde3e9',
                            borderRadius: '8px', cursor: 'pointer',
                            fontWeight: wsSelectionne?.id === ws.id ? 700 : 500,
                            color: wsSelectionne?.id === ws.id ? 'var(--SEIZO-bleu)' : 'var(--SEIZO-dark)',
                            fontSize: '0.9rem', transition: 'all 0.15s',
                        }}
                    >
                        {ws.name}
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#8A9BAB', fontWeight: 400, marginTop: '0.1rem' }}>
                            {ws.totems_count ?? 0} écran{(ws.totems_count ?? 0) > 1 ? 's' : ''}
                        </span>
                    </button>
                ))}
            </div>

            {/* Colonne droite : membres du workspace sélectionné */}
            <div style={{ flex: 1 }}>
                {wsSelectionne && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, color: 'var(--SEIZO-dark)', fontSize: '1.05rem' }}>
                                {wsSelectionne.name}
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#8A9BAB', fontWeight: 400 }}>
                                    — {membres.length} membre{membres.length > 1 ? 's' : ''}
                                </span>
                            </h3>
                            <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={() => { setEditEcrans(!editEcrans); setShowAjouter(false); setAlert(null); }}
                                    style={{ background: '#fff', border: '1.5px solid var(--SEIZO-bleu)', color: 'var(--SEIZO-bleu)', borderRadius: '8px', padding: '0.6rem 1rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
                                >
                                    {editEcrans ? 'Fermer les écrans' : 'Gérer les écrans'}
                                </button>
                                <BtnPrimary onClick={() => { setShowAjouter(!showAjouter); setEditEcrans(false); setAlert(null); }}>
                                    {showAjouter ? 'Annuler' : '+ Ajouter un membre'}
                                </BtnPrimary>
                            </div>
                        </div>

                        <Alert type={alert?.type} message={alert?.message} />

                        {/* Écrans rattachés : visibilité et affectation dans le même espace de travail. */}
                        <Card style={{ padding: '1.2rem', marginBottom: '1rem', border: editEcrans ? '2px solid var(--SEIZO-bleu)' : '1px solid #e7edf2' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
                                <div>
                                    <h4 style={{ margin: 0, color: 'var(--SEIZO-dark)', fontSize: '0.98rem' }}>Écrans du workspace</h4>
                                    <p style={{ margin: '0.3rem 0 0', color: '#6B7A87', fontSize: '0.83rem' }}>
                                        {editEcrans ? 'Sélectionnez les écrans rattachés à ce workspace.' : 'Les écrans ci-dessous sont rattachés à ce workspace.'}
                                    </p>
                                </div>
                                <span style={{ background: '#EAF6FF', color: 'var(--SEIZO-bleu)', borderRadius: '16px', padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 700 }}>
                                    {editEcrans ? selectedTotemIds.length : totemsWs.length} écran{(editEcrans ? selectedTotemIds.length : totemsWs.length) > 1 ? 's' : ''}
                                </span>
                            </div>

                            {loadingTotems ? (
                                <p style={{ margin: 0, color: '#8A9BAB', fontSize: '0.9rem' }}>Chargement des écrans...</p>
                            ) : editEcrans ? (
                                <>
                                    {totemsOrg.length === 0 ? (
                                        <p style={{ margin: 0, color: '#8A9BAB', fontSize: '0.9rem' }}>Aucun écran disponible dans cet espace.</p>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.65rem' }}>
                                            {totemsOrg.map(totem => {
                                                const selected = selectedTotemIds.includes(String(totem.id_totem));
                                                const workspaceLabel = workspaceLabelForTotem(totem);
                                                return (
                                                    <label key={totem.id_totem} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', padding: '0.75rem', borderRadius: '9px', border: selected ? '2px solid var(--SEIZO-bleu)' : '1px solid #dde3e9', background: selected ? '#F2FAFF' : '#fff', cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={selected} onChange={() => handleToggleTotem(totem.id_totem)} style={{ marginTop: '0.2rem', accentColor: 'var(--SEIZO-bleu)' }} />
                                                        <span>
                                                            <strong style={{ display: 'block', color: 'var(--SEIZO-dark)', fontSize: '0.88rem' }}>{totem.tot_nom || 'Écran sans nom'}</strong>
                                                            {workspaceLabel && <span style={{ display: 'block', color: '#8A9BAB', fontSize: '0.76rem', marginTop: '0.15rem' }}>Actuellement : {workspaceLabel}</span>}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '0.7rem', marginTop: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                        <button type="button" onClick={() => { setEditEcrans(false); setSelectedTotemIds(totemsWs.map(totem => String(totem.id_totem))); }} style={{ background: '#fff', border: '1.5px solid #dde3e9', color: '#52616d', borderRadius: '8px', padding: '0.55rem 1rem', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
                                        <BtnPrimary onClick={handleSaveTotems} loading={savingEcrans}>Enregistrer les écrans</BtnPrimary>
                                    </div>
                                </>
                            ) : totemsWs.length === 0 ? (
                                <p style={{ margin: 0, color: '#8A9BAB', fontSize: '0.9rem' }}>Aucun écran n’est rattaché à ce workspace.</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.55rem' }}>
                                    {totemsWs.map(totem => (
                                        <span key={totem.id_totem} style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.15rem', minWidth: '150px', padding: '0.65rem 0.75rem', background: '#F5F8FC', border: '1px solid #E3EAF0', borderRadius: '8px' }}>
                                            <strong style={{ color: 'var(--SEIZO-dark)', fontSize: '0.85rem' }}>{totem.tot_nom || 'Écran sans nom'}</strong>
                                            {(totem.tot_batiment || totem.tot_service) && <span style={{ color: '#8A9BAB', fontSize: '0.75rem' }}>{[totem.tot_batiment, totem.tot_service].filter(Boolean).join(' · ')}</span>}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </Card>

                        {/* Formulaire ajout membre */}
                        {showAjouter && (
                            <Card style={{ border: '2px solid var(--SEIZO-bleu)', marginBottom: '1rem' }}>
                                <h4 style={{ marginTop: 0, color: 'var(--SEIZO-dark)' }}>Ajouter un membre au workspace</h4>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--SEIZO-dark)', fontSize: '0.875rem' }}>Membre *</label>
                                    <select
                                        value={ajout.user_id}
                                        onChange={e => setAjout({ ...ajout, user_id: e.target.value })}
                                        style={{ width: '100%', padding: '0.6rem 0.9rem', borderRadius: '8px', border: '1.5px solid #dde3e9', fontSize: '0.95rem', background: '#fff' }}
                                    >
                                        <option value="">-- Sélectionner un membre --</option>
                                        {membresDisponibles.map(m => (
                                            <option key={m.id} value={m.id}>{m.prenom} {m.nom} ({m.email})</option>
                                        ))}
                                    </select>
                                    {membresDisponibles.length === 0 && (
                                        <p style={{ fontSize: '0.85rem', color: '#8A9BAB', marginTop: '0.4rem' }}>Tous les membres de l'organisation sont déjà dans ce workspace.</p>
                                    )}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--SEIZO-dark)', fontSize: '0.875rem' }}>Rôle dans le workspace</label>
                                    <select
                                        value={ajout.role}
                                        onChange={e => setAjout({ ...ajout, role: e.target.value })}
                                        style={{ width: '100%', padding: '0.6rem 0.9rem', borderRadius: '8px', border: '1.5px solid #dde3e9', fontSize: '0.95rem', background: '#fff' }}
                                    >
                                        <option value="admin">Administrateur — accès complet</option>
                                        <option value="manager">Manager — gère les écrans et peut inviter</option>
                                        <option value="viewer">Lecteur — consultation uniquement</option>
                                    </select>
                                </div>
                                <BtnPrimary onClick={handleAjouterMembre} loading={ajoutEnCours}>Ajouter au workspace</BtnPrimary>
                            </Card>
                        )}

                        {/* Tableau des membres */}
                        {loadingMembres ? (
                            <p style={{ color: '#888' }}>Chargement des membres...</p>
                        ) : (
                            <Card style={{ padding: 0, overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--SEIZO-dark)', color: '#fff' }}>
                                            {['Membre', 'Rôle', ''].map(h => (
                                                <th key={h} style={{ padding: '0.9rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {membres.map((m, i) => (
                                            <tr key={m.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fbfd', borderBottom: '1px solid #eef1f5' }}>
                                                <td style={{ padding: '0.8rem 1rem' }}>
                                                    <div style={{ fontWeight: 600, color: 'var(--SEIZO-dark)' }}>{m.prenom} {m.nom}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#8A9BAB' }}>{m.email}</div>
                                                </td>
                                                <td style={{ padding: '0.8rem 1rem' }}>
                                                    <select
                                                        value={m.role}
                                                        onChange={e => handleChangerRole(m.id, e.target.value)}
                                                        style={{
                                                            padding: '0.3rem 0.6rem', borderRadius: '6px',
                                                            border: `1.5px solid ${ROLE_WS_COLORS[m.role] || '#dde3e9'}`,
                                                            fontSize: '0.85rem', background: '#fff',
                                                            color: ROLE_WS_COLORS[m.role] || 'var(--SEIZO-dark)',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        <option value="admin">Administrateur</option>
                                                        <option value="manager">Manager</option>
                                                        <option value="viewer">Lecteur</option>
                                                    </select>
                                                </td>

                                                <td style={{ padding: '0.8rem 1rem' }}>
                                                    <BtnDanger onClick={() => handleRetirerMembre(m.id, m.email)}>Retirer</BtnDanger>
                                                </td>
                                            </tr>
                                        ))}
                                        {membres.length === 0 && (
                                            <tr><td colSpan={3} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>Aucun membre dans ce workspace.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Page principale MonCompte ────────────────────────────────────────────────
// Onglets disponibles selon le rôle (issue #274 + fix #equipe) :
// - user    : Mon Profil + Mon Espace uniquement
// - manager : + Workspaces & Droits
// - admin / super_admin : tous les onglets dont Mon Équipe
const getOnglets = () => {
    const roleName = getRoleName();
    const isAdmin = roleName === 'admin' || roleName === 'super_admin';
    const isUser  = roleName === 'user';
    return [
        { id: 'profil',      label: 'Mon Profil' },
        { id: 'entreprise',  label: 'Mon Espace' },
        ...(isAdmin ? [{ id: 'equipe', label: 'Mon Équipe' }] : []),
        ...(!isUser ? [{ id: 'workspaces', label: 'Workspaces & Droits' }] : []),
    ];
};

const MonCompte = () => {
    const history  = useHistory();
    const location = useLocation();
    const initOnglet = new URLSearchParams(location.search).get('onglet') || 'profil';
    const ONGLETS = getOnglets();
    const [ongletActif, setOngletActif] = useState(initOnglet);
    const [entrepriseCode, setEntrepriseCode] = useState(getEntrepriseCode());

    // #498 — Relire le code de l'espace actif depuis le cookie à chaque navigation
    // (nécessaire pour les Super-Admins qui changent d'espace depuis le back-office)
    useEffect(() => {
        const code = getEntrepriseCode();
        setEntrepriseCode(code);
    }, [location.key]);

    const handleLogout = () => {
        document.cookie.split(';').forEach(c => {
            const name = c.split('=')[0].trim();
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
        });
        window.location.href = '/';
    };

    const handleRetour = () => {
        history.push('/app');
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: 'inherit' }}>
            {/* Header */}
            <div style={{
                background: 'var(--SEIZO-dark)', padding: '0 2rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                height: '60px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', cursor: 'pointer' }} onClick={handleRetour}>
                    <img src={logo_seizo_s} alt="SEIZO" style={{ height: '32px', display: 'block' }} />
                    <img src={logo_seizo_nom} alt="SEIZO" style={{ height: '18px', display: 'block' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button onClick={handleRetour} style={{
                        background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
                        color: '#fff', borderRadius: '8px', padding: '0.4rem 1rem',
                        cursor: 'pointer', fontSize: '0.85rem',
                    }}>
                        ← Retour
                    </button>
                    <button onClick={handleLogout} style={{
                        background: 'var(--SEIZO-rouge)', border: 'none',
                        color: '#fff', borderRadius: '8px', padding: '0.4rem 1rem',
                        cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                    }}>
                        Déconnexion
                    </button>
                </div>
            </div>

            {/* Contenu */}
            <div style={{ maxWidth: (['equipe', 'workspaces'].includes(ongletActif) ? '1100px' : '860px'), margin: '0 auto', padding: '2.5rem 1.5rem' }}>
                <h1 style={{ color: 'var(--SEIZO-dark)', marginTop: 0, marginBottom: '2rem', fontSize: '1.6rem', fontWeight: 800 }}>
                    Mon Compte
                </h1>

                {/* Onglets */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid #dde3e9' }}>
                    {ONGLETS.filter(o => ONGLETS.find(x => x.id === o.id)).map(o => (
                        <button
                            key={o.id}
                            onClick={() => setOngletActif(o.id)}
                            style={{
                                background: 'transparent', border: 'none',
                                padding: '0.7rem 1.4rem', cursor: 'pointer',
                                fontWeight: ongletActif === o.id ? 700 : 500,
                                color: ongletActif === o.id ? 'var(--SEIZO-bleu)' : '#666',
                                borderBottom: ongletActif === o.id ? '2px solid var(--SEIZO-bleu)' : '2px solid transparent',
                                marginBottom: '-2px', fontSize: '0.95rem', transition: 'all 0.15s',
                            }}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>

                {/* Contenu de l'onglet actif */}
                {ongletActif === 'profil'      && <OngletProfil      entrepriseCode={entrepriseCode} />}
                {ongletActif === 'entreprise'  && <OngletEntreprise  entrepriseCode={entrepriseCode} />}
                {ongletActif === 'equipe'      && <OngletEquipe      entrepriseCode={entrepriseCode} />}
                {ongletActif === 'workspaces'  && <OngletWorkspaces  entrepriseCode={entrepriseCode} />}
            </div>
        </div>
    );
};

export default MonCompte;
